// §4 S6 — Calibration drift, /production/calibration (Module 04, slice 04-C-2).
//
// Work-centre picker (D-073: the work centre IS the machine grain until module
// 08 ships its equipment registry) → the drift chart: daily/weekly average
// `variance_pct` as ONE line with its min/max band, threshold guides at the
// D-083 defaults, and the trailing-30-day status the SERVER computes through
// the SAME domain-kernel decision the weekly cron runs — the breach badge here
// can never disagree with the emitted `production.calibration_drift_detected`.
// Below the chart: the recent-barrels table (label, claimed, measured,
// variance, worker, method) — also the chart's accessible table view.
//
// CHART DISCIPLINE (dataviz method): a single series needs no legend box (the
// title names it); the line is 2px in the app accent; the band is the same hue
// at low opacity; grid/axes are recessive (--border/--muted); every point
// carries a native tooltip; text wears text tokens, never the series color.
// The breach badge is a STATUS, not a series color — icon + words, never color
// alone. Roles: facility_admin / maintenance / planner / group_admin
// (`production.telemetry.read` — 0024's code, reused per D-074).

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { SegmentedControl } from "../../../../components/select";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { FacilityFilter } from "../components/facility-filter";
import {
  useCalibrationDrift,
  useFacilities,
  useWorkCenters,
  type CalibrationDrift,
  type CalibrationRecord,
} from "../hooks/production-queries";
import {
  formatBusinessDate,
  formatMinor,
  formatQty,
  formatTimestamp,
  localeLabel,
} from "../lib/format";
import { csvConventionFor, downloadCsv, toCsv, type CsvConvention } from "../../../../lib/csv";
import { formatPercent } from "../../../../lib/quantity";

export function CalibrationScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();

  const facilities = useFacilities();
  const [facilityId, setFacilityId] = useState<string | undefined>(undefined);
  const effectiveFacility = facilityId ?? facilities.data?.items[0]?.id;
  // ACTIVE only: this select is a picker, and an archived station is one no
  // barrel is measured at any more. It used to list them (04-C-2 gate finding),
  // so the default selection could land on a machine that no longer runs and
  // show an empty drift chart that read as "no drift".
  const workCenters = useWorkCenters(effectiveFacility, "active");
  const [workCenterId, setWorkCenterId] = useState<string | undefined>(undefined);
  const [bucket, setBucket] = useState<"day" | "week">("day");

  const stations = workCenters.data?.items ?? [];
  const effectiveWorkCenter =
    workCenterId !== undefined && stations.some((wc) => wc.id === workCenterId)
      ? workCenterId
      : stations[0]?.id;

  const drift = useCalibrationDrift(effectiveWorkCenter, bucket);

  useEffect(() => {
    if (drift.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [drift.error, navigate]);

  return (
    <section className="calibration-screen" data-testid="calibration-screen">
      <PageHeader title={t("production.calibration.title")} />

      <div className="calibration-filters" data-testid="calibration-filters">
        {/* ONE facility control for the whole area (F11): the §3.10 Field
            grammar, the same one S1 and S4 use. This row used to hand-roll its
            own stacked caption in production.css, so the area shipped two
            different facility filters across four sibling screens. */}
        <FacilityFilter
          value={effectiveFacility ?? ""}
          onChange={(next) => {
            setFacilityId(next);
            setWorkCenterId(undefined);
          }}
          facilities={facilities.data?.items ?? []}
          testId="calibration-facility"
        />
        <Field label={term("work_center")}>
          <select
            value={effectiveWorkCenter ?? ""}
            data-testid="calibration-work-center"
            onChange={(e) => {
              setWorkCenterId(e.target.value);
            }}
          >
            {stations.map((wc) => (
              <option key={wc.id} value={wc.id}>
                {localeLabel(wc.name_i18n, i18n.language) || wc.code}
              </option>
            ))}
          </select>
        </Field>
        {/* A small exclusive switch is the §3.10 SegmentedControl, not two
            buttons taking turns wearing `.btn-primary` (one primary per view).
            Same group role and accessible name; options keep their labels and
            `aria-pressed`, so a spec drives them by name within the group. */}
        <SegmentedControl
          options={[
            { value: "day", label: t("production.calibration.bucketDay") },
            { value: "week", label: t("production.calibration.bucketWeek") },
          ]}
          value={bucket}
          onChange={setBucket}
          label={t("production.calibration.chartTitle")}
          data-testid="calibration-bucket"
        />
      </div>

      {effectiveWorkCenter === undefined && !workCenters.isPending ? (
        <EmptyState
          title={t("production.calibration.pickWorkCenterTitle")}
          body={t("production.calibration.pickWorkCenterBody")}
        />
      ) : null}

      {drift.isPending && effectiveWorkCenter !== undefined ? (
        <TableSkeleton rows={5} label={t("production.common.loading")} />
      ) : null}

      {drift.error && !(drift.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("production.calibration.error")}
          detail={drift.error instanceof Error ? drift.error.message : undefined}
          retryLabel={t("production.common.retry")}
          onRetry={() => void drift.refetch()}
          requestId={requestIdOf(drift.error)}
          requestIdLabel={t("production.common.requestId")}
        />
      ) : null}

      {drift.data !== undefined ? <DriftBody data={drift.data} /> : null}
    </section>
  );
}

function DriftBody({ data }: { data: CalibrationDrift }) {
  const { t, i18n } = useTranslation();
  const status = data.drift;
  const breach = status.classification;
  // A COMPUTED ZERO AND AN UNKNOWN ARE DIFFERENT FACTS (surface review F10).
  // The wire carries `mean_variance_pct` / `stddev_pct` as plain numbers, and
  // over an EMPTY window they arrive as 0 — so a station nobody has measured
  // rendered "0.00%" mean, "0.00%" spread and a money impact of zero under the
  // words "Not judged": four tiles asserting a perfectly calibrated machine
  // that has never been checked. The mean and spread of no barrels do not
  // exist, so they are rendered as unknown; the barrel count (0) and the
  // thresholds are real facts and stay. Same class as the sidebar counts being
  // exact-or-absent.
  const measured = status.barrel_count > 0;

  return (
    <>
      <div
        className="calibration-status"
        data-testid="calibration-status"
        data-measured={String(measured)}
      >
        <div className="calibration-stat">
          <span className="calibration-stat-label">{t("production.calibration.meanLabel")}</span>
          <strong data-testid="calibration-mean">
            {measured
              ? formatPercent(status.mean_variance_pct, { precision: 2, locale: i18n.language })
              : t("production.calibration.noBarrelsValue")}
          </strong>
        </div>
        <div className="calibration-stat">
          <span className="calibration-stat-label">{t("production.calibration.stddevLabel")}</span>
          <strong data-testid="calibration-stddev">
            {measured
              ? formatPercent(status.stddev_pct, { precision: 2, locale: i18n.language })
              : t("production.calibration.noBarrelsValue")}
          </strong>
        </div>
        <div className="calibration-stat">
          <span className="calibration-stat-label">{t("production.calibration.barrelsLabel")}</span>
          <strong data-testid="calibration-barrels">{status.barrel_count}</strong>
        </div>
        {/* §10.4's amendment: the barrel count and the MONEY IMPACT belong on
            the same screen as the breach. The raw minor units ride a data
            attribute beside the formatted text — the wire is integer minor
            units (the money convention) and a locale-formatted string is not a number a
            reader (or a walkthrough) can check the ledger against.
            REDACTION IS KEY ABSENCE (§7): `maintenance` reads this chart with
            no money grant, and the whole TILE goes rather than rendering a
            dash — a "—" where money belongs reads as "no impact". */}
        {status.est_cost_impact_minor !== undefined && status.currency !== undefined ? (
          <div className="calibration-stat">
            <span className="calibration-stat-label">
              {t("production.calibration.impactLabel")}
            </span>
            <strong
              data-testid="calibration-impact"
              data-impact-minor={String(status.est_cost_impact_minor)}
              data-currency={status.currency}
            >
              {formatMinor(status.est_cost_impact_minor, status.currency, i18n.language)}
            </strong>
          </div>
        ) : null}
        <div className="calibration-stat">
          <span className="calibration-stat-label">
            {t("production.calibration.thresholdsLabel")}
          </span>
          <strong>
            {t("production.calibration.thresholdsValue", {
              mean: status.mean_threshold_pct,
              stddev: status.stddev_threshold_pct,
            })}
          </strong>
        </div>
      </div>
      {/* The thresholds are platform standards, not a per-site setting, and the
          note says why — a site loosening the limit its own meter is judged by
          is the gaming this signal exists to catch. */}
      <p className="calibration-thresholds-note" data-testid="calibration-thresholds-note">
        {t("production.calibration.thresholdsStandard")}
      </p>

      {breach !== null ? (
        // A STATUS chip — icon + words, never color alone (dataviz rule).
        <p
          className={`calibration-breach is-${breach.replace(/_/gu, "-")}`}
          role="status"
          data-testid="calibration-breach"
        >
          ⚠ {t(`production.calibration.classification.${breach}`)}
        </p>
      ) : (
        <p className="calibration-ok" role="status" data-testid="calibration-ok">
          {/* BELOW THE FLOOR IS NOT "FINE". Under `min_barrels` records the
              kernel does not classify at all, so `classification: null` means
              UNEVALUATED — and four barrels averaging −12 % would otherwise
              render as "within thresholds", a false statement about the
              machine. The floor comes from the server (D-083 §2.8), never a
              client copy, so a facility-configurable floor needs no web
              change. */}
          {status.barrel_count < status.min_barrels
            ? t("production.calibration.statusBelowFloor", { count: status.min_barrels })
            : t("production.calibration.statusOk")}
        </p>
      )}

      {data.buckets.length === 0 ? (
        <EmptyState
          title={t("production.calibration.emptyTitle")}
          body={t("production.calibration.emptyBody")}
        />
      ) : (
        <DriftChart
          buckets={data.buckets}
          meanThreshold={status.mean_threshold_pct}
          title={t("production.calibration.chartTitle")}
          aria={t("production.calibration.chartAria")}
          guideLabel={t("production.calibration.thresholdGuide", {
            pct: status.mean_threshold_pct,
          })}
        />
      )}

      <h2>{t("production.calibration.recentTitle")}</h2>
      {data.records_recent.length === 0 ? (
        <EmptyState
          title={t("production.calibration.emptyTitle")}
          body={t("production.calibration.emptyBody")}
        />
      ) : (
        <>
          <RecordsTable records={data.records_recent} locale={i18n.language} />
          <Button
            data-testid="calibration-csv"
            onClick={() => {
              downloadRecordsCsv(
                data.records_recent,
                data.work_center_id,
                csvConventionFor(i18n.language),
              );
            }}
          >
            {t("production.calibration.csvExport")}
          </Button>
        </>
      )}
    </>
  );
}

/** §4 S6's CSV export — the served rows, verbatim (exact decimal strings; a
 * spreadsheet re-deriving variance would defeat the GENERATED columns). Built
 * by the shared export (lib/csv.ts, D-289 ruling 11), so it gains the formula
 * guard and the UTF-8 BOM the hand-rolled copy lacked: a batch label is typed
 * by an operator and a Cyrillic one opened as mojibake in Excel. */
function downloadRecordsCsv(
  records: readonly CalibrationRecord[],
  workCenterId: string,
  convention: CsvConvention,
): void {
  const header = [
    "measured_at",
    "batch_label",
    "machine_claimed_qty",
    "measured_qty",
    "uom",
    "variance_qty",
    "variance_pct",
    "measurement_method",
    "measured_by",
    "capture_source",
  ];
  const rows = records.map((r) => [
    r.measured_at,
    r.batch_label,
    r.machine_claimed_qty,
    r.measured_qty,
    r.uom,
    r.variance_qty,
    r.variance_pct ?? "",
    r.measurement_method,
    r.measured_by,
    r.capture_source,
  ]);
  downloadCsv(
    `calibration-${workCenterId}.csv`,
    toCsv(header, rows, {
      convention,
      decimalHeaders: ["machine_claimed_qty", "measured_qty", "variance_qty", "variance_pct"],
    }),
  );
}

/**
 * The drift chart, hand-rolled SVG (no chart library exists in this repo and
 * one screen does not justify one): daily avg `variance_pct` as a 2px accent
 * line with ≥8px point markers, the min/max envelope as a low-opacity band,
 * a zero baseline, and dashed threshold guides at ±mean-threshold. One y-axis
 * (dataviz rule #1); barrel counts ride the point tooltips rather than a
 * second axis. Recessive grid in --border; all text in --muted/--fg.
 */
function DriftChart({
  buckets,
  meanThreshold,
  title,
  aria,
  guideLabel,
}: {
  buckets: CalibrationDrift["buckets"];
  meanThreshold: number;
  title: string;
  aria: string;
  guideLabel: string;
}) {
  const { t, i18n } = useTranslation();
  const pct = (v: number): string => formatPercent(v, { precision: 2, locale: i18n.language });
  const width = 720;
  const height = 260;
  const pad = { top: 16, right: 16, bottom: 28, left: 44 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const values = buckets.flatMap((b) => [b.min_variance_pct, b.max_variance_pct]);
  const rawMin = Math.min(...values, -meanThreshold);
  const rawMax = Math.max(...values, meanThreshold);
  const span = Math.max(rawMax - rawMin, 1);
  const yMin = rawMin - span * 0.1;
  const yMax = rawMax + span * 0.1;

  const x = (i: number): number =>
    pad.left + (buckets.length === 1 ? plotW / 2 : (i / (buckets.length - 1)) * plotW);
  const y = (v: number): number => pad.top + ((yMax - v) / (yMax - yMin)) * plotH;

  const linePath = buckets
    .map((b, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(b.avg_variance_pct).toFixed(1)}`)
    .join(" ");
  const bandPath =
    buckets
      .map((b, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(b.max_variance_pct).toFixed(1)}`)
      .join(" ") +
    " " +
    [...buckets]
      .reverse()
      .map((b, i) => `L${x(buckets.length - 1 - i).toFixed(1)},${y(b.min_variance_pct).toFixed(1)}`)
      .join(" ") +
    " Z";

  const guides = [meanThreshold, -meanThreshold].filter((g) => g >= yMin && g <= yMax);

  return (
    <figure className="calibration-chart" data-testid="calibration-chart">
      <figcaption>{title}</figcaption>
      <svg
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        role="img"
        aria-label={aria}
        className="calibration-chart-svg"
      >
        {/* zero baseline + recessive frame */}
        {0 >= yMin && 0 <= yMax ? (
          <line
            x1={pad.left}
            x2={width - pad.right}
            y1={y(0)}
            y2={y(0)}
            className="calibration-axis"
          />
        ) : null}
        {/* threshold guides (±3% by default), dashed, labelled once */}
        {guides.map((g) => (
          <g key={g}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={y(g)}
              y2={y(g)}
              className="calibration-guide"
            />
            <text x={width - pad.right} y={y(g) - 4} textAnchor="end" className="calibration-text">
              {g > 0 ? guideLabel : ""}
            </text>
          </g>
        ))}
        {/* y ticks: min / 0 / max */}
        {[yMin + span * 0.1, 0, yMax - span * 0.1].map((v) => (
          <text key={v} x={pad.left - 6} y={y(v) + 3} textAnchor="end" className="calibration-text">
            {formatPercent(v, { precision: 1, locale: i18n.language })}
          </text>
        ))}
        {/* min/max envelope */}
        <path d={bandPath} className="calibration-band" />
        {/* the series */}
        <path d={linePath} className="calibration-line" fill="none" />
        {buckets.map((b, i) => (
          <circle
            key={b.date}
            cx={x(i)}
            cy={y(b.avg_variance_pct)}
            r={4}
            className="calibration-dot"
          >
            <title>
              {`${formatBusinessDate(b.date, i18n.language)} · ${pct(b.avg_variance_pct)} (${pct(b.min_variance_pct)}…${pct(b.max_variance_pct)}) · ${String(b.barrel_count)} ${t("production.calibration.barrelsLabel")}`}
            </title>
          </circle>
        ))}
        {/* x labels: first + last bucket */}
        <text x={pad.left} y={height - 8} className="calibration-text">
          {buckets[0]?.date}
        </text>
        <text x={width - pad.right} y={height - 8} textAnchor="end" className="calibration-text">
          {buckets[buckets.length - 1]?.date}
        </text>
      </svg>
    </figure>
  );
}

export function RecordsTable({
  records,
  locale,
}: {
  records: readonly CalibrationRecord[];
  locale: string;
}) {
  const { t } = useTranslation();

  // Every cell now carries the label its header used to supply — the previous
  // hand-written table had none at all, so at 412 px the eight values were
  // anonymous numbers under a dropped header row.
  const columns: DataTableColumn<CalibrationRecord>[] = [
    {
      key: "measured_at",
      header: t("production.calibration.col.measuredAt"),
      render: (record) => formatTimestamp(record.measured_at, locale),
    },
    { key: "batch_label", header: t("production.calibration.col.batch"), id: true },
    {
      key: "machine_claimed_qty",
      header: t("production.calibration.col.claimed"),
      numeric: true,
      render: (record) => `${formatQty(record.machine_claimed_qty, { locale })} ${record.uom}`,
    },
    {
      key: "measured_qty",
      header: t("production.calibration.col.measured"),
      numeric: true,
      render: (record) => `${formatQty(record.measured_qty, { locale })} ${record.uom}`,
    },
    {
      key: "variance_qty",
      header: t("production.calibration.col.variance"),
      numeric: true,
      render: (record) => `${formatQty(record.variance_qty, { locale })} ${record.uom}`,
    },
    {
      key: "variance_pct",
      header: t("production.calibration.col.variancePct"),
      numeric: true,
      render: (record) => record.variance_pct ?? t("production.common.notAvailable"),
    },
    {
      key: "measurement_method",
      header: t("production.calibration.col.method"),
      render: (record) => t(`production.calibration.method.${record.measurement_method}`),
    },
    {
      // D-084: a barrel can be measured at the station or keyed off the paper
      // completion sheet. Both are real measurements and both count toward
      // drift — but "the tablet said 5980" and "someone typed 5980" are never
      // the same fact, so the table says which.
      key: "capture_source",
      header: t("production.detail.ops.colSource"),
      render: (record) => t(`production.captureSource.${record.capture_source}`),
    },
  ];

  return (
    <DataTable
      data-testid="calibration-records"
      columns={columns}
      rows={records}
      rowKey={(record) => record.id}
      empty={null}
      rowProps={(record) => ({ "data-testid": `calibration-row-${record.batch_label}` })}
    />
  );
}
